import { FC } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Typography } from '@mui/material';
import { professionNavigation } from 'const';
import { useAppSelector, useUserFunctionality } from 'hooks';
import { Button } from 'portfolio3/ui-kit/button';

import { RecommendationExcursionsFilters } from '../../components/filters';
import GenericEntityList from '../../components/GenericEntityList';
import GenericRecommendationCard from '../../components/GenericRecommendationCard';
import NoDataPlug from '../../components/NoDataPlug';
import { proforientationParticipationDialogActions } from '../../model/actions';
import { proforientationRecommendationSelectors } from '../../model/selectors';
import { getFilterOptions, getFilterStoreKey } from '../../utils/filters';

const RecommendationExcursionsWidget: FC = () => {
  const dispatch = useDispatch();
  const excursions = useAppSelector(proforientationRecommendationSelectors.excursions);
  const filters = useAppSelector((state) => state.proforientationRecommendationFilters);

  const { isStudent } = useUserFunctionality();

  const industries = excursions.map((event) => event.industry);
  const industryOptions = getFilterOptions(industries);

  const districts = excursions.map((event) => event.district);
  const districtOptions = getFilterOptions(districts);

  const filteredExcursions = excursions.filter((event) => {
    const { industry, district } = event;

    const filterIndustries =
      (filters[getFilterStoreKey('excursions', 'industry', 'applied')] as string | undefined) ?? null;
    const filterDistricts =
      (filters[getFilterStoreKey('excursions', 'district', 'applied')] as string | undefined) ?? null;

    const filterIndustryCodes = filterIndustries?.split(',').map(Number) ?? [];
    const filterDistrictCodes = filterDistricts?.split(',').map(Number) ?? [];

    const isContainIndustry = filterIndustryCodes.some((code) => {
      const option = industryOptions.find((option) => option.code === code);
      return option?.value === industry;
    });
    const isContainDistrict = filterDistrictCodes.some((code) => {
      const option = districtOptions.find((option) => option.code === code);
      return option?.value === district;
    });

    return (
      (isContainIndustry || filterIndustryCodes.length === 0) && (isContainDistrict || filterDistrictCodes.length === 0)
    );
  });

  const filtersElement = (
    <RecommendationExcursionsFilters industryOptions={industryOptions} districtOptions={districtOptions} />
  );

  const listElement = (
    <GenericEntityList
      containerId={`${professionNavigation.recommendations.id}/excursions`}
      entities={filteredExcursions}
      initialCardCount={3}
      renderEntityCard={(entity) => {
        const handleSendApplication = () => {
          dispatch(proforientationParticipationDialogActions.setParticipationEventId(entity.eventId));
        };

        return (
          <GenericRecommendationCard
            key={entity.eventId}
            date={entity.formattedDate}
            industry={entity.industry}
            name={entity.name}
            description={entity.description}
            place={entity.place}
            district={entity.district}
            illustration={entity.illustration}
            buttonElement={isStudent && <Button onClick={handleSendApplication}>Хочу пойти</Button>}
          />
        );
      }}
    />
  );

  const emptyFilterResultsElement = (
    <Box sx={{ display: 'grid', placeItems: 'center', paddingBlock: '26px' }}>
      <NoDataPlug title="Ничего не найдено" description="Попробуйте изменить поисковый запрос" />
    </Box>
  );

  if (excursions.length === 0) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center' }}>
        <NoDataPlug
          title="Пройдены все доступные мероприятия"
          description="Пройденные мероприятия можно посмотреть ниже в блоке «Профориентационные мероприятия»"
        />
      </Box>
    );
  }

  return (
    <>
      <Typography variant="Paragraph LG/Regular">Рекомендации обновляются ежедневно</Typography>

      <Box marginTop="20px">{filtersElement}</Box>

      {filteredExcursions.length > 0 ? <Box marginTop="20px">{listElement}</Box> : emptyFilterResultsElement}
    </>
  );
};

export default RecommendationExcursionsWidget;
